import React from "react";
import {
  Box,
  Chip,
  Link,
  Select,
  MenuItem,
  Pagination,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { SkeletonLoadingTable, VisibleContent } from "../../../../components";
import {
  getAddressToComplexUrl,
} from "../../../../utils/sectionNavigation";
import {
  formatAddressDisplay,
  getLocationAddressText,
  getLocationClasses,
  getLocationCoordinatesLabel,
  getLocationDevices,
  getLocationEntityTypeLabels,
  getLocationRecordId,
} from "../../../../utils/addressFields";

const classColors = {
  'Стационарный': { backgroundColor: '#E4FBF1', color: '#1CA673' },
  'Мобильный': { backgroundColor: '#FFF9E6', color: '#FFC107' },
  'Передвижной': { backgroundColor: '#E3F2FD', color: '#2196F3' },
};

const useStyles = makeStyles(() => ({
  paginationBar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 0" },
  paginationInfo: { fontSize: 14, color: "#71717A" },
  paginationRight: { display: "flex", alignItems: "center", gap: 24 },
  paginationLimit: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#71717A" },
  limitSelect: { minWidth: 70, height: 32, fontSize: 14 },
  paginationPages: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#71717A" },
  multilineCell: { display: "flex", flexDirection: "column", gap: 2 },
  serialLink: {
    color: "#4F39F6",
    textDecoration: "underline",
    fontSize: 14,
    lineHeight: "20px",
    whiteSpace: "nowrap",
    "&:hover": { color: "#432DD7" },
  },
  serialText: { fontSize: 14, lineHeight: "20px", whiteSpace: "nowrap" },
  typeLine: { fontSize: 14, lineHeight: "20px" },
}));

const COLUMNS = [
  { key: "location", label: "Местоположение", sortable: true },
  { key: "coordinates", label: "Координаты", sortable: false },
  { key: "entity_type", label: "Комплекс/Камера", sortable: true },
  { key: "class", label: "Тип комплекса", sortable: true },
  { key: "complex_serial", label: "Номер устройства", sortable: true },
];

const AddressTable = ({ items, filter, isLoading, totalCount, totalPages, onChangeFilter, onRowClick, selectedItemId }) => {
  const classes = useStyles();

  React.useEffect(() => {
    if (!selectedItemId || isLoading) return undefined;
    const targetId = String(selectedItemId);
    const hasRow = (items || []).some((item) => {
      const rowId = getLocationRecordId(item) || item.id;
      return rowId != null && String(rowId) === targetId;
    });
    if (!hasRow) return undefined;

    const scrollToRow = () => {
      const row = window.document.querySelector(`[data-location-id="${CSS.escape(targetId)}"]`);
      if (!row) return false;
      if (row.getBoundingClientRect().height < 1) return false;
      row.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      return true;
    };

    let attempts = 0;
    const maxAttempts = 12;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (scrollToRow() || attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 100);

    return () => window.clearInterval(timer);
  }, [selectedItemId, items, isLoading]);

  const handleChangePage = (e, p) => {
    onChangeFilter("page", p, true);
  };

  const handleChangeLimit = (e) => {
    onChangeFilter("limit", e.target.value, true);
  };

  const handleSort = (columnKey) => {
    if (filter.sort_field === columnKey) {
      // Если уже сортируем по этому полю, меняем направление
      if (filter.sort_direction === "asc") {
        onChangeFilter("sort_direction", "desc", true);
      } else {
        // Третий клик - сброс сортировки
        onChangeFilter("sort_field", "");
        onChangeFilter("sort_direction", "asc", true);
      }
    } else {
      // Первый клик по новому полю - сортировка по возрастанию
      onChangeFilter("sort_field", columnKey);
      onChangeFilter("sort_direction", "asc", true);
    }
  };

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell key={column.key}>
                {column.sortable ? (
                  <TableSortLabel
                    active={filter.sort_field === column.key}
                    direction={filter.sort_field === column.key ? filter.sort_direction : "asc"}
                    onClick={() => handleSort(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <VisibleContent visible={!isLoading}>
            {items.map((item) => {
              const rowId = getLocationRecordId(item) || item.id;
              return (
                <AddressRow
                  key={rowId}
                  item={item}
                  isSelected={selectedItemId != null && String(selectedItemId) === String(rowId)}
                  onClick={() => onRowClick(item)}
                />
              );
            })}
          </VisibleContent>
          <VisibleContent visible={isLoading}>
            <SkeletonLoadingTable countRows={10} countColumns={5} />
          </VisibleContent>
        </TableBody>
      </Table>

      <div className={classes.paginationBar}>
        <span className={classes.paginationInfo}>Найдено {totalCount.toLocaleString("ru-RU")} записей</span>
        <div className={classes.paginationRight}>
          <div className={classes.paginationLimit}>
            <span>Выводить по</span>
            <Select value={filter.limit} onChange={handleChangeLimit} size="small" className={classes.limitSelect}>
              <MenuItem value={15}>15</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </div>
          {totalPages > 1 && (
            <div className={classes.paginationPages}>
              <span>Страница {filter.page} из {totalPages}</span>
              <Pagination count={totalPages} page={filter.page} onChange={handleChangePage} size="small" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const AddressRow = React.memo(({ item, isSelected, onClick }) => {
  const classes = useStyles();
  const devices = getLocationDevices(item);
  const entityTypes = getLocationEntityTypeLabels(item);
  const locationClasses = getLocationClasses(item);
  const coordinates = getLocationCoordinatesLabel(item);
  const locationText = formatAddressDisplay(
    getLocationAddressText(item) || item.location || item.address || item.address_cam
  );

  const rowId = getLocationRecordId(item) || item.id;

  return (
    <TableRow
      hover
      onClick={onClick}
      data-location-id={rowId != null ? String(rowId) : undefined}
      className={isSelected ? "--selected" : ""}
      sx={{ cursor: "pointer" }}
    >
      <TableCell>{locationText}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{coordinates}</TableCell>
      <TableCell>
        <div className={classes.multilineCell}>
          {(entityTypes.length > 0 ? entityTypes : ["Н/Д"]).map((label, index) => (
            <span key={`type-${index}`} className={classes.typeLine}>
              {label}
              {index < entityTypes.length - 1 ? "," : ""}
            </span>
          ))}
        </div>
      </TableCell>
      <TableCell>
        <div className={classes.multilineCell}>
          {(locationClasses.length > 0 ? locationClasses : ["Н/Д"]).map((classLabel, index) => {
            const colors = classColors[classLabel] || { backgroundColor: "#F4F4F5", color: "#71717A" };
            return (
              <Chip
                key={`class-${index}-${classLabel}`}
                label={classLabel}
                size="small"
                sx={{ fontWeight: 600, fontSize: 12, width: "fit-content", ...colors }}
              />
            );
          })}
        </div>
      </TableCell>
      <TableCell>
        <div className={classes.multilineCell}>
          {(devices.length > 0 ? devices : [{ serial: "", id: null }]).map((device, index) => {
            const serial = device.serial || "Н/Д";
            const href = getAddressToComplexUrl(item, device);
            const isLast = index >= (devices.length || 1) - 1;
            if (href && device.serial) {
              return (
                <Link
                  key={device.id || `serial-${index}`}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.serialLink}
                  onClick={(e) => e.stopPropagation()}
                >
                  {serial}{isLast ? "" : ","}
                </Link>
              );
            }
            return (
              <Box
                key={device.id || `serial-${index}`}
                component="span"
                className={classes.serialText}
              >
                {serial}{isLast ? "" : ","}
              </Box>
            );
          })}
        </div>
      </TableCell>
    </TableRow>
  );
});
AddressRow.displayName = "AddressRow";

export default AddressTable;
